import { useEffect, useMemo } from 'react';

import {
  usePlayerResolvedAnimation,
  usePlayerSettings,
} from '@cartwhl/3d/player';
import type { FrameRate } from '@cartwhl/common/types';

import {
  getSourceBvhForTimeline,
  resolveBvhFrameRate,
} from '../keyPoses/keyPoseFrameMapping';
import type { MotionEditorState } from '../motionEditorStore';
import {
  arePlayerPlaybackSettingsEqual,
  resolveMotionEditorPlayerPlaybackSettings,
} from '../store/scenePlaybackUtils';

interface UseScenePlaybackSettingsSyncParams {
  actorId?: string;
  hasRouteSceneID: boolean;
  scene: MotionEditorState['scene'];
  timelineIndex?: number | null;
}

export function useScenePlaybackSettingsSync({
  actorId,
  hasRouteSceneID,
  scene,
  timelineIndex,
}: UseScenePlaybackSettingsSyncParams) {
  const { settings: playerSettings, setSettings: setPlayerSettings } =
    usePlayerSettings();
  const resolvedAnimation = usePlayerResolvedAnimation(actorId);
  const bvhFrameRate = useMemo(
    () =>
      resolveBvhFrameRate(
        getSourceBvhForTimeline(
          resolvedAnimation.bvh,
          resolvedAnimation.bvhs,
          timelineIndex,
        ),
      ),
    [resolvedAnimation.bvh, resolvedAnimation.bvhs, timelineIndex],
  );

  const nextPlayback = useMemo(() => {
    const scenePlayback = resolveMotionEditorPlayerPlaybackSettings({
      hasRouteSceneID,
      scene,
    });
    if (!scenePlayback) {
      return null;
    }

    return {
      ...scenePlayback,
      frameRate: bvhFrameRate ?? scenePlayback.frameRate,
    } satisfies { frameRate: FrameRate; frameStepSize: number };
  }, [
    bvhFrameRate,
    hasRouteSceneID,
    scene.data?.sceneID,
    scene.data?.sceneSettings?.playbackSettings?.frameRate,
    scene.data?.sceneSettings?.playbackSettings?.frameStepSize,
    scene.status,
  ]);

  useEffect(() => {
    if (!nextPlayback) {
      return;
    }

    if (arePlayerPlaybackSettingsEqual(playerSettings.playback, nextPlayback)) {
      return;
    }

    setPlayerSettings({ playback: nextPlayback });
  }, [
    nextPlayback,
    playerSettings.playback.frameRate,
    playerSettings.playback.frameStepSize,
    setPlayerSettings,
  ]);
}
